import { ExternalLinkIcon } from "@chakra-ui/icons";
import {
  Box,
  Divider,
  Heading,
  HStack,
  Link,
  Text,
  VStack,
} from "@chakra-ui/react";
import { motion, useReducedMotion } from "framer-motion";
import Education from "./Education";
import Work from "./Work";

const MotionBox = motion(Box);

const sectionTitleStyles = {
  fontSize: ["sm", "md"],
  letterSpacing: "0.2em",
  textTransform: "uppercase",
  color: "gray.800",
  borderBottom: "2px solid",
  borderColor: "darkBlue",
  pb: 2,
  width: "fit-content",
};

const container = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.09, delayChildren: 0.06 },
  },
};

const item = {
  hidden: { opacity: 0, y: 18 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] },
  },
};

const instant = {
  hidden: { opacity: 1, y: 0 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { staggerChildren: 0, delayChildren: 0 },
  },
};

const instantItem = {
  hidden: { opacity: 1, y: 0 },
  visible: { opacity: 1, y: 0, transition: { duration: 0 } },
};

const Resume = () => {
  const reduceMotion = useReducedMotion();
  const containerVariants = reduceMotion ? instant : container;
  const itemVariants = reduceMotion ? instantItem : item;

  return (
    <Box
      className="resume"
      px={["6%", "10%", "15%"]}
      py={{ base: 24, md: 28 }}
    >
      <MotionBox
        maxW="900px"
        mx="auto"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-80px" }}
      >
        <MotionBox variants={itemVariants}>
          <HStack
            justify="space-between"
            align={{ base: "start", sm: "center" }}
            flexDir={{ base: "column", sm: "row" }}
            gap={4}
            mb={10}
            textAlign={{ base: "left", sm: "inherit" }}
          >
            <Box>
              <Heading
                as="h2"
                fontFamily="Title"
                fontSize={["2xl", "3xl"]}
                color="gray.800"
                fontWeight="normal"
              >
                Résumé
              </Heading>
              <Text fontSize={["xs", "sm"]} color="gray.700" mt={1}>
                Experience and education
              </Text>
            </Box>
            <Link
              href={encodeURI("/Negin Alipanahi.pdf")}
              isExternal
              display="inline-flex"
              alignItems="center"
              gap={2}
              px={4}
              py={2}
              borderRadius="md"
              borderWidth="1px"
              borderColor="gray.700"
              color="gray.800"
              fontSize="sm"
              fontWeight="600"
              transition="all 0.25s ease"
              _hover={{
                borderColor: "darkBlue",
                color: "gray.900",
                bg: "whiteAlpha.500",
              }}
            >
              Download PDF
              <ExternalLinkIcon boxSize={3} />
            </Link>
          </HStack>
        </MotionBox>

        <MotionBox variants={itemVariants}>
          <Text as="h3" {...sectionTitleStyles} mb={6}>
            Professional experience
          </Text>
        </MotionBox>

        <VStack align="stretch" spacing={10} mb={12}>
          <MotionBox variants={itemVariants}>
            <Work
              title="Front-end Developer"
              company="EAIGLE Inc. | Toronto"
              date="Dec 2024 – Present"
            >
              <Box as="ul" listStyleType="none" pl={0} sx={{ li: { mb: 3 } }}>
                <li>
                  Built React/TypeScript interfaces for an Automated Vehicle
                  Access Control (AVAC) system supporting multi-site and
                  multi-client logistics workflows.
                </li>
                <li>
                  Designed mobile-first, PWA, and touch-screen kiosk experiences
                  for driver check-in, identity verification, and gate operations
                  in real-world environments.
                </li>
                <li>
                  Managed data flow between frontend and backend systems using
                  REST APIs, covering vehicle tracking, registration, and access
                  control logic.
                </li>
                <li>
                  Translated client SOPs and operational requirements into
                  structured product behavior, including validation rules,
                  workflow logic, and edge-case handling.
                </li>
              </Box>
            </Work>
          </MotionBox>

          <Divider borderColor="blackAlpha.300" />

          <MotionBox variants={itemVariants}>
            <Work
              title="Industrial Engineer"
              subTitle="Operations & Product Coordination"
              company="Pelak Co. | Tehran"
              date="Mar 2022 – May 2024"
            >
              <Box as="ul" listStyleType="none" pl={0} sx={{ li: { mb: 3 } }}>
                <li>
                  Translated real-world service workflows (booking, pricing,
                  fulfillment) into structured product requirements for a
                  digital platform.
                </li>
                <li>
                  Identified operational bottlenecks and user pain points,
                  contributing to process and product improvements that enhanced
                  customer and provider experience.
                </li>
                <li>
                  Worked across product, engineering, and operations teams to
                  align feature delivery with business priorities and real-world
                  constraints.
                </li>
                <li>
                  Coordinated end-to-end initiatives, tracking progress, resolving
                  blockers, and maintaining momentum in a fast-paced startup
                  environment.
                </li>
              </Box>
            </Work>
          </MotionBox>

          <Divider borderColor="blackAlpha.300" />

          <MotionBox variants={itemVariants}>
            <Work
              title="Industrial Engineer"
              subTitle="Assistant Project Manager"
              company="Golandisheh IT Co. | Tehran"
              date="Aug 2014 – Mar 2018"
            >
              <Box as="ul" listStyleType="none" pl={0} sx={{ li: { mb: 3 } }}>
                <li>
                  Supported project planning and execution, coordinating across
                  stakeholders and cross-functional teams.
                </li>
                <li>
                  Tracked timelines, deliverables, and progress to ensure
                  alignment and timely completion of project milestones.
                </li>
                <li>
                  Analyzed operational processes and contributed to improvements
                  that increased efficiency and streamlined workflows.
                </li>
              </Box>
            </Work>
          </MotionBox>
        </VStack>

        <MotionBox variants={itemVariants}>
          <Text as="h3" {...sectionTitleStyles} mb={6}>
            Education
          </Text>
        </MotionBox>

        <MotionBox variants={itemVariants}>
          <VStack align="stretch" spacing={4}>
            <Education major="M.Sc. Industrial Engineering — System Management and Productivity" />
            <Education major="B.Sc. Industrial Engineering — Planning and System Analysis" />
          </VStack>
        </MotionBox>
      </MotionBox>
    </Box>
  );
};

export default Resume;
